import { call, takeLatest, select } from 'redux-saga/effects';
import config, { HTTP, alerts } from '../config';
import { SUBMIT } from '../actions/types';
import { submitFormSuccess, setAlertMessage, setErrorResponse } from '../actions';

import { selectSubmitVals } from '../utilities/reselecter';
import { savePaymentMethod, submitApp } from './api';
import { fetchSaga } from './fetchSaga';
import { getPublicUserId } from '../utilities';

const submitActions = {
  eResponse: submitFormSuccess,
  eError: setErrorResponse,
  eAlert: setAlertMessage,
};

export const apiSubmitForm = fetchSaga(
  submitActions,
  function* (): any {
    const {
      formData: { amount, paymentMethod, paymentType, debitDate, accountBsb, accountNumber, accountName },
      initData,
      identity,
      transactionReference,
    } = yield select(selectSubmitVals);

    const { applicationId } = initData;
    const userId = getPublicUserId(initData);
    const { accessToken } = identity;

    if (paymentMethod === 'EP') {
      // Compatibility guard for form state persisted (via @brighte/brighte-one-core's
      // saveState, wired up in store/index.tsx) before this deploy, when 'EP' was a sentinel
      // meaning "use the account's existing method" and mapped to methodId: null server-side. A
      // real id is required now (accountDetailsPaymentOption carries accountDetails.paymentMethodId
      // instead), so a leftover 'EP' means stale persisted state. Return a 422 Response instead
      // of putting an alert directly — this generator's return value becomes fetchSaga's
      // "response", which then dispatches the alert itself; returning here without one would
      // fall through to fetchSaga's success path (SUBMIT_SUCCESS clears the session). A JSON
      // body is required — a null body would throw if anything downstream calls .json() on it.
      // fetchSaga ignores the body for a 422 with no matching customHandler and always shows
      // alerts.UNPROCESSABLE_ENTITY, so the body's message is kept in sync with that rather than
      // a bespoke string that would never actually reach the user.
      return new Response(JSON.stringify({ message: alerts.UNPROCESSABLE_ENTITY }), {
        status: HTTP.UNPROCESSABLE_ENTITY,
        statusText: 'Unprocessable Entity',
      });
    }

    if (paymentMethod === 'NEW' && paymentType !== 'DD') {
      // 'NEW' is only ever meant to reach here with paymentType 'DD' — the 'CC' path replaces
      // paymentMethod with the real tokenized id as soon as savePaymentMethod succeeds (see
      // apiSavePaymentMethods), so 'NEW' should never still be paired with 'CC' by the time this
      // saga runs. Guard against a race/stale-state reaching this point the same way the 'EP'
      // guard above does, rather than sending the literal 'NEW' as methodId to the gateway.
      return new Response(JSON.stringify({ message: alerts.UNPROCESSABLE_ENTITY }), {
        status: HTTP.UNPROCESSABLE_ENTITY,
        statusText: 'Unprocessable Entity',
      });
    }

    // paymentMethod is otherwise always a real payment method id now.
    let methodId: string = paymentMethod;

    if (paymentMethod === 'NEW' && paymentType === 'DD') {
      const response = yield call(savePaymentMethod, {
        accessToken,
        userId,
        type: 'debit',
        source: config.appType,
        accountBsb,
        accountName,
        accountNumber,
      });
      const { ok } = response;
      if (!ok) {
        // Surface the save failure directly instead of falling through with the literal 'NEW'
        // sentinel as methodId, which would otherwise trigger a second, more confusing failure
        // from makePayment (invalid UUID) that masks the real problem.
        return response;
      }
      const { id } = yield response.json();
      methodId = id;
    }

    const submitObj: Record<string, any> = {
      accessToken,
      source: config.appType,
      amount: parseFloat(parseFloat(amount).toFixed(2)),
      userId,
      loanId: applicationId,
      transactionReference,
      methodId,
      paymentDate: debitDate === 'today' ? null : new Date(parseInt(debitDate)).toISOString(),
    };

    return yield call(submitApp, submitObj);
  },
  [
    { code: HTTP.CREATED, message: 'success', event: 'success' },
    // finance-core's makePayment never surfaces a downstream failure as a GraphQL error — it
    // catches everything and returns a normal { status: 500, statusText } response (see
    // wrapCatch in payments.resolver.ts). fetchSaga's switch(status) has no case for 500, so
    // without this it would fall through to the unmatched-status branch and dispatch
    // eResponse (success) with the failure body — silently showing a successful payment.
    { code: HTTP.INTERNAL_SERVER_ERROR, message: alerts.FAILED_PAYMENT, event: 'alert' },
  ],
);

export function* formSubmission(): Generator<any> {
  yield call(apiSubmitForm);
}

function* watchFormActions(): Generator<any> {
  yield takeLatest(SUBMIT, formSubmission);
}

export default watchFormActions;
